import z from 'zod';

import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import type {
  MaterializationOutcome,
  MaterializationResult,
  MaterializationSubject,
  TaskInstance,
  TaskMaterializationResponse,
  TaskResetResponse,
  TaskWriteResponse,
} from './taskSchemas';
import { parseTaskInstance, parseTaskInstances, TaskSubjectSchema } from './taskSchemas';

/** Client for the qcs-tasks endpoints exposed through the shared gateway. */

const TaskResourcesEnvelopeSchema = z.object({
  resources: z.array(z.unknown()),
});

const TaskWriteResponseSchema = z.object({
  changed: z.array(z.unknown()),
});

const MaterializationResultSchema = z.object({
  subject: TaskSubjectSchema,
  outcome: z.string(),
  instance: z.unknown().optional(),
  reason: z.string().optional(),
});

const TaskMaterializationResponseSchema = z.object({
  results: z.array(MaterializationResultSchema),
});

const TaskResetResponseSchema = z.object({
  deletedCount: z.number().int().nonnegative(),
});

const KNOWN_OUTCOMES: ReadonlySet<string> = new Set<MaterializationOutcome>([
  'CREATED',
  'EXISTING',
  'RETIRED',
  'REJECTED',
]);

const isKnownOutcome = (outcome: string): outcome is MaterializationOutcome => KNOWN_OUTCOMES.has(outcome);

/** Writes are not constrained by the dashboard read budget, but remain bounded. */
const WRITE_TIMEOUT_MS = 5000;

/** Prevents an invalidated collection read from blocking its coalesced follow-up indefinitely. */
const READ_TIMEOUT_MS = 30_000;

/** A failed instance transition, including its HTTP status when available. */
export class TaskWriteError extends Error {
  readonly status: number | undefined;

  /** Redeclared to make the inherited ES2022 `cause` required and read-only. */
  override readonly cause: unknown;

  constructor(action: string, id: string, cause: unknown) {
    const status = statusOf(cause);
    super(`Failed to ${action} task instance ${id}${status ? ` (HTTP ${status})` : ''}`);
    this.name = 'TaskWriteError';
    this.status = status;
    this.cause = cause;
  }

  get shouldRefetch(): boolean {
    return this.status === 403 || this.status === 404 || this.status === 410;
  }
}

/** A failed materialization batch, classified separately from instance writes. */
export class TaskMaterializationError extends Error {
  readonly status: number | undefined;

  override readonly cause: unknown;

  constructor(taskKey: string, cause: unknown) {
    const status = statusOf(cause);
    super(`Failed to materialize instances of ${taskKey}${status ? ` (HTTP ${status})` : ''}`);
    this.name = 'TaskMaterializationError';
    this.status = status;
    this.cause = cause;
  }

  /** 404 and 409 indicate that this build and the task catalog disagree. */
  get isContractMismatch(): boolean {
    return this.status === 404 || this.status === 409;
  }

  get isEligibilityChanged(): boolean {
    return this.status === 403;
  }
}

/** A failed development-only reset of the authenticated dataset's task state. */
export class TaskResetError extends Error {
  readonly status: number | undefined;

  override readonly cause: unknown;

  constructor(cause: unknown) {
    const status = statusOf(cause);
    super(`Failed to reset task state${status ? ` (HTTP ${status})` : ''}`);
    this.name = 'TaskResetError';
    this.status = status;
    this.cause = cause;
  }
}

const statusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;
  const { response } = error as { response?: { status?: unknown } };
  return typeof response?.status === 'number' ? response.status : undefined;
};

type InstanceWriteAction = 'complete' | 'dismiss' | 'undismiss';

const instanceUrl = (id: string, action: InstanceWriteAction): string =>
  `${getServiceUrl()}/tasks/instances/${encodeURIComponent(id)}/${action}`;

/** Exported so development diagnostics can show the routed endpoint. */
export const tasksUrl = (): string => `${getServiceUrl()}/tasks`;

/**
 * Writes intentionally have no AbortSignal: navigation may cancel a read, but
 * should not cancel an action the user already submitted.
 */
const writeInstance = async (id: string, action: InstanceWriteAction): Promise<TaskWriteResponse> => {
  let qcsResponse;
  try {
    qcsResponse = await httpClientInterface.send('POST', instanceUrl(id, action), undefined, undefined, undefined, {
      timeout: WRITE_TIMEOUT_MS,
    });
  } catch (error) {
    throw new TaskWriteError(action, id, error);
  }

  const response = TaskWriteResponseSchema.parse(qcsResponse.data);
  return { changed: parseTaskInstances(response.changed) };
};

/**
 * Materializes a batch for any per-subject task. Per-subject rejection is a
 * successful response so other subjects in the batch are preserved.
 */
const materializeInstances = async (
  taskKey: string,
  subjects: readonly MaterializationSubject[],
): Promise<TaskMaterializationResponse> => {
  let qcsResponse;
  try {
    qcsResponse = await httpClientInterface.send(
      'POST',
      `${getServiceUrl()}/tasks/${encodeURIComponent(taskKey)}/instances`,
      { subjects },
      undefined,
      undefined,
      { timeout: WRITE_TIMEOUT_MS },
    );
  } catch (error) {
    throw new TaskMaterializationError(taskKey, error);
  }

  const response = TaskMaterializationResponseSchema.parse(qcsResponse.data);

  return {
    results: response.results.flatMap<MaterializationResult>((result) => {
      if (!isKnownOutcome(result.outcome)) return [];

      // Keep a known outcome even when its attached instance is unreadable.
      const instance = parseTaskInstance(result.instance);

      return [{ ...result, outcome: result.outcome, instance }];
    }),
  };
};

export default {
  async fetch(signal?: AbortSignal): Promise<TaskInstance[]> {
    const qcsResponse = await httpClientInterface.send('GET', tasksUrl(), undefined, undefined, undefined, {
      signal,
      timeout: READ_TIMEOUT_MS,
    });

    const response = TaskResourcesEnvelopeSchema.parse(qcsResponse.data);
    return parseTaskInstances(response.resources);
  },

  /** Completion is idempotent for a persisted task instance. */
  complete(id: string): Promise<TaskWriteResponse> {
    return writeInstance(id, 'complete');
  },

  dismiss(id: string): Promise<TaskWriteResponse> {
    return writeInstance(id, 'dismiss');
  },

  undismiss(id: string): Promise<TaskWriteResponse> {
    return writeInstance(id, 'undismiss');
  },

  materialize(taskKey: string, subjects: readonly MaterializationSubject[]): Promise<TaskMaterializationResponse> {
    return materializeInstances(taskKey, subjects);
  },

  /** Available only in environments where the service explicitly enables its reset endpoint. */
  async reset(): Promise<TaskResetResponse> {
    let qcsResponse;
    try {
      qcsResponse = await httpClientInterface.send(
        'POST',
        `${tasksUrl()}/internal/reset`,
        undefined,
        undefined,
        undefined,
        { timeout: WRITE_TIMEOUT_MS },
      );
    } catch (error) {
      throw new TaskResetError(error);
    }

    return TaskResetResponseSchema.parse(qcsResponse.data);
  },
} as const;
